import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Toolbar, ToolbarGroup, ToolbarSpacer } from "../components/Toolbar";
import {
  fetchReviewQueue,
  publishPacks,
  PUBLISH_PACKS_PER_INVOKE,
  reviewPacks,
  unpublishPacks,
  type PublishBatchResult,
  type ReviewQueueResponse,
} from "../lib/publishApi";
import { currentUtcMonth, shiftMonth } from "../lib/readingsApi";
import type { PackStatus } from "../lib/types";

type StatusFilter = ReviewQueueResponse["statusFilter"];
type BusyAction = "review" | "publish" | "unpublish" | null;

export function PublishPage() {
  const [month, setMonth] = useState(currentUtcMonth);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [queue, setQueue] = useState<ReviewQueueResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<BusyAction>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PublishBatchResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchReviewQueue(month, statusFilter);
      setQueue(data);
      setSelected(new Set());
    } catch (err) {
      setQueue(null);
      setError(err instanceof Error ? err.message : "Failed to load review queue.");
    } finally {
      setLoading(false);
    }
  }, [month, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const packs = queue?.packs ?? [];
  const allSelected = packs.length > 0 && packs.every((p) => selected.has(p.recordName));

  const selectionSummary = useMemo(() => {
    const rows = packs.filter((p) => selected.has(p.recordName));
    return {
      count: rows.length,
      draft: rows.filter((p) => p.status === "draft").length,
      reviewed: rows.filter((p) => p.status === "reviewed").length,
      published: rows.filter((p) => p.status === "published").length,
    };
  }, [packs, selected]);

  function toggle(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(packs.map((p) => p.recordName)));
  }

  async function runAction(action: Exclude<BusyAction, null>) {
    const names = [...selected];
    if (names.length === 0) {
      setError("Select at least one pack.");
      return;
    }
    setBusy(action);
    setError(null);
    setResult(null);
    setProgress(null);
    try {
      const onProgress = (p: { chunkIndex: number; chunkTotal: number }) => {
        setProgress(`Chunk ${p.chunkIndex}/${p.chunkTotal}`);
      };
      const data =
        action === "review"
          ? await reviewPacks(names, onProgress)
          : action === "publish"
            ? await publishPacks(names, onProgress)
            : await unpublishPacks(names, onProgress);
      setResult(data);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed.");
    } finally {
      setBusy(null);
      setProgress(null);
    }
  }

  const canAdvance = selectionSummary.draft + selectionSummary.reviewed > 0;

  return (
    <AppShell>
      <PageHeader
        eyebrow="Workflow"
        title="Review & publish"
        description={
          <>
            Mark drafts reviewed, then publish to CloudKit Public. Batches of{" "}
            <strong>{PUBLISH_PACKS_PER_INVOKE}</strong> packs per request.
          </>
        }
        actions={
          <Toolbar label="Queue actions">
            <ToolbarGroup>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                disabled={!!busy}
                aria-label="Previous month"
              >
                ←
              </button>
              <button type="button" className="btn btn-quiet btn-sm" disabled>
                {month}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                disabled={!!busy}
                aria-label="Next month"
              >
                →
              </button>
            </ToolbarGroup>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => setMonth(currentUtcMonth())}
              disabled={!!busy}
            >
              This month
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={!!busy || loading}
              onClick={() => void load()}
            >
              Refresh
            </button>
          </Toolbar>
        }
      />

      <div className="panel panel-pad stack" style={{ marginBottom: 16 }}>
        <Toolbar label="Filters and actions">
          <div className="publish-filters" role="group" aria-label="Status filter">
            {(
              [
                ["pending", "Pending"],
                ["draft", "Draft"],
                ["reviewed", "Reviewed"],
                ["published", "Published"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`btn btn-sm ${statusFilter === value ? "btn-primary" : "btn-quiet"}`}
                onClick={() => setStatusFilter(value)}
                disabled={!!busy}
              >
                {label}
              </button>
            ))}
          </div>
          <ToolbarSpacer />
          <ToolbarGroup>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={!!busy || !canAdvance}
              onClick={() => void runAction("review")}
            >
              {busy === "review" ? "…" : "Mark reviewed"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!!busy || !canAdvance}
              onClick={() => void runAction("publish")}
            >
              {busy === "publish" ? "…" : "Publish"}
            </button>
          </ToolbarGroup>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={!!busy || selectionSummary.published === 0}
            onClick={() => void runAction("unpublish")}
          >
            {busy === "unpublish" ? "…" : "Unpublish"}
          </button>
        </Toolbar>
        {selectionSummary.count > 0 && (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {selectionSummary.count} selected
            {progress ? ` · ${progress}` : ""}
          </p>
        )}
      </div>

      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {queue?.warnings?.map((warning) => (
        <div key={warning} className="error-banner" role="status" style={{ marginBottom: 12 }}>
          {warning}
        </div>
      ))}

      {result && (
        <div
          className={result.failed > 0 ? "error-banner" : "banner-ok"}
          role="status"
          style={{ marginBottom: 12 }}
        >
          <code>{result.action}</code> · {result.succeeded} ok · {result.failed} failed
          {result.errorSummary ? ` · ${result.errorSummary}` : ""}
        </div>
      )}

      {loading && !queue ? (
        <div className="panel panel-pad" role="status">
          Loading queue…
        </div>
      ) : packs.length === 0 ? (
        <div className="panel empty-panel">
          <h2>No packs</h2>
          <p>
            Nothing matches <code>{statusFilter}</code> for {month}. Scrape drafts or open the
            calendar.
          </p>
          <Toolbar>
            <Link className="btn btn-primary" to="/scrape">
              Scrape
            </Link>
            <Link className="btn btn-quiet" to="/readings">
              Calendar
            </Link>
          </Toolbar>
        </div>
      ) : (
        <div className="panel panel-pad stack">
          <label className="publish-select-all">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!!busy} />
            <span>Select all ({packs.length})</span>
          </label>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>Date</th>
                  <th>Lang</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {packs.map((pack) => (
                  <tr key={pack.recordName}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(pack.recordName)}
                        onChange={() => toggle(pack.recordName)}
                        disabled={!!busy}
                        aria-label={`Select ${pack.recordName}`}
                      />
                    </td>
                    <td>
                      <Link className="link-inline" to={`/readings/${pack.dateKey}`}>
                        {pack.dateKey}
                      </Link>
                    </td>
                    <td>
                      <code>{pack.languageCode}</code>
                    </td>
                    <td>{pack.liturgicalTitle ?? "—"}</td>
                    <td>
                      <span className={`chip ${statusTone(pack.status)}`}>{pack.status}</span>
                    </td>
                    <td className="muted">{pack.source ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function statusTone(status: PackStatus): string {
  if (status === "published") return "";
  if (status === "reviewed") return "warm";
  return "neutral";
}
